import { FunctionComponent, useMemo } from "react";

import { checkPasswordComplexity } from "check-password-complexity";

import { PASSWORD_MIN_LENGTH } from "@/constants";
import { InputField } from "@/components/InputField/InputField";
import { classNames } from "@/utils/classNames";

/** The score at which a password satisfies every complexity rule and the submit gate opens. */
export const PASSWORD_COMPLEXITY_REQUIRED_SCORE = 5;

/**
 * Live complexity score (0–5) for the given password. `check-password-complexity` uses a strict
 * `length > minLength` rule, so to require our canonical 8-char minimum (PASSWORD_REGEX
 * `(?=.{8,})` + backend `@MinLength(8)`) we pass minLength = 7. Without this the gate demanded
 * 9+ chars while the UI said "at least 8", leaving a valid 8-char password unable to enable the
 * button.
 */
export const usePasswordComplexity = (password: string): number =>
	useMemo(() => {
		if (!password) {
			return 0;
		}

		return checkPasswordComplexity(password, { minLength: PASSWORD_MIN_LENGTH - 1 }).score;
	}, [password]);

interface PasswordComplexityFieldProps {
	password: string;
	complexity: number;
	label: string;
	placeholder: string;
	onChange: (value: string) => void;
	onEnter: () => void;
}

/**
 * The password input + three-tile strength meter + live requirement hints shared by the
 * invitation (SetPassword) and forgotten-password (ChangePassword) flows, so both pages present
 * the identical checks. The submit button of the host page should stay disabled until
 * `complexity >= PASSWORD_COMPLEXITY_REQUIRED_SCORE`.
 */
export const PasswordComplexityField: FunctionComponent<PasswordComplexityFieldProps> = ({
	password,
	complexity,
	label,
	placeholder,
	onChange,
	onEnter,
}) => {
	const tileColor = useMemo(() => {
		if (complexity >= 5) {
			return "bg-status-success";
		} else if (complexity >= 3) {
			return "bg-status-warning";
		} else if (complexity >= 1) {
			return "bg-status-error";
		}

		return "bg-gray-200";
	}, [complexity]);

	const passwordIncludesNumbers = useMemo(() => {
		return password.match(/\d/);
	}, [password]);

	const passwordIncludesSpecialCharacters = useMemo(() => {
		return password.match(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/);
	}, [password]);

	const passwordIncludesUppercase = useMemo(() => {
		return password.match(/[A-Z]/);
	}, [password]);

	const passwordIncludesLowercase = useMemo(() => {
		return password.match(/[a-z]/);
	}, [password]);

	return (
		<>
			<div className="text-left text-xs font-normal w-full mb-4">
				<InputField
					handleChange={(event) => onChange(event.target.value)}
					label={label}
					name="password"
					placeholder={placeholder}
					type="password"
					value={password}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							onEnter();
						}
					}}
				/>
				<div className="flex flex-row gap-2">
					<div
						className={classNames(
							"w-1/3 h-[3px] bg-gray-200 rounded-md",
							complexity >= 1 ? tileColor : "bg-gray-200"
						)}
					></div>
					<div
						className={classNames(
							"w-1/3 h-[3px] bg-gray-200 rounded-md",
							complexity >= 3 ? tileColor : "bg-gray-200"
						)}
					></div>
					<div
						className={classNames(
							"w-1/3 h-[3px] bg-gray-200 rounded-md",
							complexity >= 5 ? tileColor : "bg-gray-200"
						)}
					></div>
				</div>
			</div>
			{password.length > 1 && (
				<div className="text-left text-xs font-normal w-full mb-4">
					{password.length < 8 && (
						<div className="text-left text-xs font-normal w-full mb-1">
							Password must contain at least 8 characters
						</div>
					)}
					{!passwordIncludesNumbers && (
						<div className="text-left text-xs font-normal w-full mb-1">
							Password must contain at least 1 number
						</div>
					)}
					{!passwordIncludesSpecialCharacters && (
						<div className="text-left text-xs font-normal w-full mb-1">
							Password must contain at least 1 special character
						</div>
					)}
					{!passwordIncludesUppercase && (
						<div className="text-left text-xs font-normal w-full mb-1">
							Password must contain at least 1 uppercase
						</div>
					)}
					{!passwordIncludesLowercase && (
						<div className="text-left text-xs font-normal w-full mb-1">
							Password must contain at least 1 lowercase
						</div>
					)}
				</div>
			)}
		</>
	);
};
